import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { LayoutDashboard, Store, Calendar, Tag, Megaphone, MapPin, FileText, Images, Mail, LogOut, DollarSign, TrendingUp, Users, Sparkles, Send, Loader2, Trophy, Trash2, Bot, ShieldAlert, RefreshCw, Star, Image as ImageIcon } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { RANGER_AVATAR } from "@/lib/constants";
import ResourceManager from "./ResourceManager";
import ContentManager from "./ContentManager";
import ImageVault from "./ImageVault";
import VenueMedia from "./VenueMedia";
import EventsReview from "./EventsReview";
import DealsReview from "./DealsReview";
import CoverageMap from "./CoverageMap";
import { toast } from "sonner";

const NAV = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "businesses", label: "Businesses", icon: Store },
  { id: "events", label: "Events", icon: Calendar },
  { id: "offers", label: "Offers", icon: Tag },
  { id: "promotions", label: "Promotions", icon: Megaphone },
  { id: "tours", label: "Tours", icon: MapPin },
  { id: "ads", label: "Advertising", icon: Megaphone },
  { id: "content", label: "Content Studio", icon: FileText },
  { id: "venuebot", label: "Venue Bot", icon: Bot },
  { id: "venuemedia", label: "Venue Media", icon: ImageIcon },
  { id: "coverage", label: "Coverage Map", icon: MapPin },
  { id: "eventsreview", label: "Events Review", icon: Calendar },
  { id: "dealsreview", label: "Deals Review", icon: Tag },
  { id: "moderation", label: "Moderation", icon: ShieldAlert },
  { id: "vault", label: "Image Vault", icon: Images },
  { id: "newsletter", label: "Newsletter", icon: Send },
  { id: "contest", label: "Photo Contest", icon: Trophy },
  { id: "subscribers", label: "Subscribers", icon: Mail },
];

const COLORS = ["#A3A3A3", "#FF0055", "#FFEA00"];

function Overview() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="text-neutral-500 py-10">Loading...</div>;

  const tierData = Object.entries(d.members_by_tier || {}).map(([k, v]) => ({ name: k, value: v }));
  const contentData = Object.entries(d.counts || {}).map(([k, v]) => ({ name: k, value: v }));

  const kpis = [
    { label: "Membership MRR", value: `$${d.mrr.toLocaleString()}`, icon: DollarSign, color: "#00FF66" },
    { label: "Ad Revenue", value: `$${(d.ad_revenue || 0).toLocaleString()}`, icon: TrendingUp, color: "#FFEA00" },
    { label: "Members (Businesses)", value: d.counts.businesses, icon: Users, color: "#FF0055" },
    { label: "Active Ads", value: d.counts.ads || 0, icon: Send, color: "#00E5FF" },
  ];

  return (
    <div>
      <h2 className="font-display font-bold text-2xl text-white mb-5">Dashboard Overview</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {kpis.map((k) => (
          <div key={k.label} className="border border-white/10 bg-[#141414] p-5" data-testid={`kpi-${k.label}`}>
            <k.icon size={20} style={{ color: k.color }} />
            <div className="font-display font-extrabold text-3xl text-white mt-3">{k.value}</div>
            <div className="text-neutral-400 text-xs mt-1">{k.label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="border border-white/10 bg-[#141414] p-6">
          <h3 className="font-accent text-[#00E5FF] text-sm mb-4">Members by Tier</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={tierData}>
              <XAxis dataKey="name" stroke="#666" fontSize={12} />
              <YAxis stroke="#666" fontSize={12} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #262626" }} />
              <Bar dataKey="value">{tierData.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="border border-white/10 bg-[#141414] p-6">
          <h3 className="font-accent text-[#00E5FF] text-sm mb-4">Content & Listings</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={contentData}>
              <XAxis dataKey="name" stroke="#666" fontSize={10} interval={0} angle={-25} textAnchor="end" height={60} />
              <YAxis stroke="#666" fontSize={12} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #262626" }} />
              <Bar dataKey="value" fill="#FF0055" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function Newsletter() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/newsletter/preview").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  const send = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/admin/newsletter/send");
      if (res.error) toast.error(res.error);
      else toast.success(`Sent to ${res.sent} subscriber(s)`);
      load();
    } catch (e) { toast.error("Send failed"); } finally { setBusy(false); }
  };
  return (
    <div>
      <h2 className="font-display font-bold text-2xl text-white mb-2">Weekly Newsletter</h2>
      <p className="text-neutral-500 text-sm mb-5 max-w-2xl">The RiNo Ranger auto-sends a "Things To Do in RiNo This Week" roundup every 7 days. You can also preview and send it on demand.</p>
      {data && !data.resend_configured && (
        <div className="border border-[#FFEA00]/40 bg-[#FFEA00]/5 text-[#FFEA00] text-sm p-4 mb-5" data-testid="resend-warning">
          Resend is not configured yet. Add your <b>RESEND_API_KEY</b> and verified <b>SENDER_EMAIL</b> to start sending. Preview works now.
        </div>
      )}
      <div className="flex items-center gap-3 mb-5">
        <button onClick={send} disabled={busy} data-testid="newsletter-send-btn" className="bg-[#FF0055] hover:bg-[#ff2d74] text-white text-sm font-semibold px-5 py-2.5 flex items-center gap-2 disabled:opacity-50">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send Now
        </button>
        {data?.last?.last_sent && <span className="text-neutral-500 text-xs">Last sent: {new Date(data.last.last_sent).toLocaleString()} ({data.last.last_count} sent)</span>}
      </div>
      {data && (
        <div className="border border-white/10 bg-white p-2 max-w-2xl" data-testid="newsletter-preview">
          <iframe title="preview" srcDoc={data.html} className="w-full h-[560px] border-0" />
        </div>
      )}
    </div>
  );
}

function AdsManager() {
  const [ads, setAds] = useState([]);
  const load = () => api.get("/admin/ads").then((r) => setAds(r.data));
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    try { await api.put(`/admin/ads/${id}/status`, { status }); toast.success(`Marked ${status}`); load(); }
    catch { toast.error("Failed"); }
  };
  const colors = { active: "#00FF66", pending: "#FFEA00", rejected: "#FF0055", expired: "#888" };
  const revenue = ads.filter((a) => a.paid).reduce((s, a) => s + (a.total || 0), 0);
  return (
    <div>
      <h2 className="font-display font-bold text-2xl text-white mb-1">Advertising <span className="text-neutral-500 text-base">({ads.length})</span></h2>
      <p className="text-neutral-500 text-sm mb-5">Paid ad revenue: <span className="text-[#00FF66] font-semibold">${revenue.toFixed(2)}</span></p>
      <div className="border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-white/10 bg-[#141414]">
            {["Placement", "Advertiser", "Total", "Paid", "Status", ""].map((h) => <th key={h} className="text-left px-4 py-3 font-accent text-[#00E5FF] text-xs">{h}</th>)}
          </tr></thead>
          <tbody>
            {ads.map((a) => (
              <tr key={a.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`admin-ad-${a.id}`}>
                <td className="px-4 py-3 text-white">{a.label}<div className="text-neutral-500 text-xs">{a.quantity} {a.unit} {a.page && `· ${a.page}`} {a.category && `· ${a.category}`}</div></td>
                <td className="px-4 py-3 text-neutral-300">{a.advertiser_name}<div className="text-neutral-500 text-xs">{a.advertiser_email}</div></td>
                <td className="px-4 py-3 text-white">${a.total}</td>
                <td className="px-4 py-3 text-neutral-300">{a.paid ? "Yes" : "No"}</td>
                <td className="px-4 py-3"><span className="text-xs font-accent px-2 py-1" style={{ color: colors[a.status], border: `1px solid ${colors[a.status]}55` }}>{a.status}</span></td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {a.status !== "active" && <button onClick={() => setStatus(a.id, "active")} data-testid={`ad-activate-${a.id}`} className="text-[#00FF66] text-xs mr-3 hover:underline">Activate</button>}
                  {a.status !== "rejected" && <button onClick={() => setStatus(a.id, "rejected")} data-testid={`ad-reject-${a.id}`} className="text-[#FF0055] text-xs hover:underline">Reject</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {ads.length === 0 && <div className="text-neutral-500 text-sm py-10 text-center">No ad bookings yet.</div>}
      </div>
    </div>
  );
}

function ContestRounds() {
  const [rounds, setRounds] = useState([]);
  const [form, setForm] = useState({ title: "", theme: "", prize: "", ends_at: "" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/contest/rounds").then((r) => setRounds(r.data));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.title || !form.ends_at) { toast.error("Title and deadline are required"); return; }
    setBusy(true);
    try {
      await api.post("/admin/contest/rounds", { ...form, ends_at: new Date(form.ends_at).toISOString() });
      toast.success("Round created"); setForm({ title: "", theme: "", prize: "", ends_at: "" }); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const openRound = async (id) => { await api.post(`/admin/contest/rounds/${id}/open`); toast.success("Round opened"); load(); };
  const closeRound = async (id) => { await api.post(`/admin/contest/rounds/${id}/close`); toast.success("Round closed — winners picked 🏆"); load(); };
  const del = async (id) => { await api.delete(`/admin/contest/rounds/${id}`); toast.success("Deleted"); load(); };

  const badge = { open: "bg-[#00E5FF] text-black", closed: "bg-neutral-600 text-white", upcoming: "bg-[#FFEA00] text-black" };
  const inp = "w-full bg-transparent border border-white/15 px-3 py-2 text-sm text-white outline-none focus:border-[#00E5FF]";

  return (
    <div className="mb-10 border border-white/10 bg-[#0f0f0f] p-5" data-testid="contest-rounds-panel">
      <h3 className="font-accent text-[#00E5FF] text-sm uppercase tracking-wider mb-4">Contest Rounds</h3>
      <form onSubmit={create} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <input placeholder="Title (e.g. July Photo Contest)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="round-title" className={inp} />
        <input placeholder="Theme" value={form.theme} onChange={(e) => setForm({ ...form, theme: e.target.value })} data-testid="round-theme" className={inp} />
        <input placeholder="Prize" value={form.prize} onChange={(e) => setForm({ ...form, prize: e.target.value })} data-testid="round-prize" className={inp} />
        <input type="datetime-local" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} data-testid="round-deadline" className={inp} />
        <button type="submit" disabled={busy} data-testid="round-create" className="bg-[#FF0055] hover:bg-[#ff2d74] text-white text-sm font-semibold px-4 py-2 disabled:opacity-50">Create + Open</button>
      </form>
      {rounds.length === 0 ? <div className="text-neutral-500 text-sm py-4 text-center">No rounds yet.</div> : (
        <div className="space-y-2">
          {rounds.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-3 border border-white/10 px-4 py-3" data-testid={`admin-round-${r.id}`}>
              <span className={`text-[10px] font-accent uppercase px-2 py-0.5 ${badge[r.status] || "bg-neutral-700 text-white"}`}>{r.status}</span>
              <span className="text-white text-sm font-medium">{r.title}</span>
              {r.theme && <span className="text-neutral-500 text-xs">{r.theme}</span>}
              <span className="text-neutral-500 text-xs">Deadline: {new Date(r.ends_at).toLocaleString()}</span>
              <span className="text-neutral-500 text-xs">· {r.entry_count} entries</span>
              <div className="ml-auto flex gap-3">
                {r.status !== "open" && <button onClick={() => openRound(r.id)} data-testid={`round-open-${r.id}`} className="text-[#00E5FF] text-xs hover:underline">Open</button>}
                {r.status === "open" && <button onClick={() => closeRound(r.id)} data-testid={`round-close-${r.id}`} className="text-[#FFEA00] text-xs hover:underline">Close + Pick Winners</button>}
                <button onClick={() => del(r.id)} data-testid={`round-delete-${r.id}`} className="text-[#FF0055] text-xs hover:underline flex items-center gap-1"><Trash2 size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ContestManager() {
  const [entries, setEntries] = useState([]);
  const load = () => api.get("/admin/contest").then((r) => setEntries(r.data));
  useEffect(() => { load(); }, []);
  const toggleWinner = async (e) => {
    await api.put(`/admin/contest/${e.id}`, { winner: !e.winner });
    toast.success(!e.winner ? "Marked as winner 🏆" : "Winner removed"); load();
  };
  const del = async (id) => { await api.delete(`/admin/contest/${id}`); toast.success("Deleted"); load(); };
  return (
    <div>
      <h2 className="font-display font-bold text-2xl text-white mb-5">Photo Contest <span className="text-neutral-500 text-base">({entries.length})</span></h2>
      <ContestRounds />
      {entries.length === 0 ? <div className="text-neutral-500 text-sm py-10 text-center border border-white/10">No entries yet.</div> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {entries.map((e) => (
            <div key={e.id} className="border border-white/10 bg-[#141414]" data-testid={`admin-contest-${e.id}`}>
              <div className="relative h-40 bg-black">
                {e.media_type === "video" ? <video src={e.url} className="w-full h-full object-cover" /> : <img src={e.url} alt="" className="w-full h-full object-cover" />}
                {e.winner && <span className="absolute top-2 left-2 bg-[#FFEA00] text-black text-[10px] font-accent px-2 py-0.5">Winner</span>}
              </div>
              <div className="p-3">
                <div className="text-white text-xs truncate">{e.caption || e.spot}</div>
                <div className="text-neutral-500 text-xs">by {e.name} · ♥ {e.likes}</div>
                <div className="flex gap-2 mt-2">
                  <button onClick={() => toggleWinner(e)} data-testid={`contest-winner-${e.id}`} className="text-[#FFEA00] text-xs hover:underline">{e.winner ? "Unpick" : "Winner"}</button>
                  <button onClick={() => del(e.id)} data-testid={`contest-delete-${e.id}`} className="text-[#FF0055] text-xs hover:underline flex items-center gap-1"><Trash2 size={12} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function VenueBot() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/venues/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, []);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/venues/ingest"); toast.success(data.message); load(); }
    catch { toast.error("Failed to start bot"); } finally { setBusy(false); }
  };
  const regen = async () => {
    try { const { data } = await api.post("/admin/venues/regenerate-guides"); toast.success(data.message); }
    catch { toast.error("Failed"); }
  };
  const harvestInfo = async () => {
    try { const { data } = await api.post("/admin/venues/harvest-info"); toast.success(data.message); }
    catch { toast.error("Failed to start"); }
  };
  const running = status?.state === "running";
  const stat = [
    { label: "Discovered", value: status?.discovered ?? "—" },
    { label: "Inserted", value: status?.inserted ?? "—" },
    { label: "Updated", value: status?.updated ?? "—" },
    { label: "Real Photos", value: status?.photos ?? "—" },
  ];
  return (
    <div data-testid="venuebot-panel">
      <h2 className="font-display font-bold text-2xl text-white mb-2">Venue Research Bot</h2>
      <p className="text-neutral-500 text-sm mb-5 max-w-2xl">
        Discovers <b>real RiNo venues</b> from OpenStreetMap and pulls each venue's own profile photo from its website — then rebuilds the interlinked category guides. Runs automatically every week.
      </p>
      <div className="flex items-center gap-3 mb-6">
        <button onClick={run} disabled={busy || running} data-testid="venuebot-run" className="bg-[#FF0055] hover:bg-[#ff2d74] text-white text-sm font-semibold px-5 py-2.5 flex items-center gap-2 disabled:opacity-50">
          {running ? <Loader2 size={15} className="animate-spin" /> : <Bot size={15} />} {running ? "Running…" : "Run Bot Now"}
        </button>
        <button onClick={regen} data-testid="venuebot-regen" className="border border-white/15 text-neutral-300 hover:text-white text-sm px-4 py-2.5 flex items-center gap-2">
          <RefreshCw size={14} /> Rebuild Guides
        </button>
        <button onClick={harvestInfo} data-testid="venuebot-harvest-info" className="border border-white/15 text-neutral-300 hover:text-white text-sm px-4 py-2.5 flex items-center gap-2">
          <RefreshCw size={14} /> Harvest Menus &amp; Events
        </button>
        {status?.state && <span className="text-neutral-500 text-xs">Status: <span className={running ? "text-[#FFEA00]" : "text-[#00FF66]"}>{status.state}</span></span>}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {stat.map((s) => (
          <div key={s.label} className="border border-white/10 bg-[#141414] p-5" data-testid={`venuebot-stat-${s.label}`}>
            <div className="font-display font-extrabold text-3xl text-white">{s.value}</div>
            <div className="text-neutral-400 text-xs mt-1">{s.label}</div>
          </div>
        ))}
      </div>
      {status?.error && <div className="border border-[#FF0055]/40 bg-[#FF0055]/5 text-[#FF0055] text-sm p-4">Error: {status.error}</div>}
      {status?.finished_at && <div className="text-neutral-500 text-xs">Last finished: {new Date(status.finished_at).toLocaleString()}</div>}
    </div>
  );
}

function Moderation() {
  const [reviews, setReviews] = useState([]);
  const [rsvps, setRsvps] = useState([]);
  const load = () => {
    api.get("/admin/reviews").then((r) => setReviews(r.data)).catch(() => {});
    api.get("/admin/rsvps").then((r) => setRsvps(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const delReview = async (id) => { await api.delete(`/admin/reviews/${id}`); toast.success("Review deleted"); load(); };
  const delRsvp = async (id) => { await api.delete(`/admin/rsvps/${id}`); toast.success("RSVP removed"); load(); };
  return (
    <div data-testid="moderation-panel">
      <h2 className="font-display font-bold text-2xl text-white mb-5">Moderation</h2>

      <h3 className="font-accent text-[#00E5FF] text-sm mb-3">Reviews ({reviews.length})</h3>
      <div className="space-y-3 mb-10">
        {reviews.length === 0 && <div className="text-neutral-500 text-sm py-6 text-center border border-white/10">No reviews to moderate.</div>}
        {reviews.map((r) => (
          <div key={r.id} className="border border-white/10 bg-[#141414] p-4 flex items-start justify-between gap-4" data-testid={`admin-review-${r.id}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-white font-medium">{r.name}</span>
                <span className="flex items-center text-[#FFEA00] text-xs">{r.rating}<Star size={11} fill="#FFEA00" className="ml-0.5" /></span>
                <span className="text-neutral-500 text-xs">on {r.business_slug}</span>
              </div>
              {r.text && <p className="text-neutral-400 text-sm mt-1">{r.text}</p>}
              {r.photos?.length > 0 && <div className="flex gap-2 mt-2">{r.photos.map((u, i) => <img key={i} src={u} alt="" className="w-12 h-12 object-cover border border-white/10" />)}</div>}
            </div>
            <button onClick={() => delReview(r.id)} data-testid={`review-delete-${r.id}`} className="text-[#FF0055] text-xs hover:underline flex items-center gap-1 flex-shrink-0"><Trash2 size={13} /> Delete</button>
          </div>
        ))}
      </div>

      <h3 className="font-accent text-[#00E5FF] text-sm mb-3">Promotion RSVPs ({rsvps.length})</h3>
      <div className="border border-white/10">
        {rsvps.length === 0 && <div className="text-neutral-500 text-sm py-6 text-center">No RSVPs yet.</div>}
        {rsvps.map((r) => (
          <div key={r.id} className="flex items-center justify-between px-4 py-3 border-b border-white/5 text-sm" data-testid={`admin-rsvp-${r.id}`}>
            <span className="text-white">{r.name || "Anonymous"} <span className="text-neutral-500 text-xs">{r.email}</span></span>
            <div className="flex items-center gap-4">
              <span className="text-neutral-500 text-xs">{r.promotion_slug}</span>
              <button onClick={() => delRsvp(r.id)} data-testid={`rsvp-delete-${r.id}`} className="text-[#FF0055] text-xs hover:underline flex items-center gap-1"><Trash2 size={12} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Subscribers() {
  const [subs, setSubs] = useState([]);
  useEffect(() => { api.get("/admin/subscribers").then((r) => setSubs(r.data)); }, []);
  return (
    <div>
      <h2 className="font-display font-bold text-2xl text-white mb-5">Subscribers <span className="text-neutral-500 text-base">({subs.length})</span></h2>
      <div className="border border-white/10">
        {subs.map((s) => (
          <div key={s.id} className="flex items-center justify-between px-4 py-3 border-b border-white/5 text-sm">
            <span className="text-white">{s.email}</span>
            <span className="text-neutral-500 text-xs">{s.name || "—"}</span>
          </div>
        ))}
        {subs.length === 0 && <div className="text-neutral-500 text-sm py-10 text-center">No subscribers yet.</div>}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");

  useEffect(() => { if (!loading && !user) nav("/admin"); }, [user, loading, nav]);
  if (loading || !user) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-neutral-500">Loading...</div>;

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      <aside className="w-60 border-r border-white/10 bg-[#0d0d0d] flex flex-col fixed h-screen">
        <div className="flex items-center gap-2 px-5 h-16 border-b border-white/10">
          <img src={RANGER_AVATAR} alt="" className="w-8 h-8 object-cover border border-[#FF0055]" />
          <span className="font-display font-extrabold text-white">VISIT<span className="text-[#FF0055]">RiNo</span></span>
        </div>
        <nav className="flex-1 py-4 overflow-y-auto">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => setTab(n.id)} data-testid={`admin-tab-${n.id}`}
              className={`w-full flex items-center gap-3 px-5 py-2.5 text-sm transition-colors ${tab === n.id ? "text-white bg-[#FF0055]/10 border-l-2 border-[#FF0055]" : "text-neutral-400 hover:text-white border-l-2 border-transparent"}`}>
              <n.icon size={17} /> {n.label}
            </button>
          ))}
        </nav>
        <button onClick={() => { logout(); nav("/admin"); }} data-testid="admin-logout" className="flex items-center gap-3 px-5 py-4 text-sm text-neutral-400 hover:text-[#FF0055] border-t border-white/10">
          <LogOut size={17} /> Sign Out
        </button>
      </aside>

      <main className="flex-1 ml-60 p-8">
        {tab === "overview" && <Overview />}
        {["businesses", "events", "offers", "promotions", "tours"].includes(tab) && <ResourceManager resource={tab} key={tab} />}
        {tab === "ads" && <AdsManager />}
        {tab === "content" && <ContentManager />}
        {tab === "venuebot" && <VenueBot />}
        {tab === "venuemedia" && <VenueMedia />}
        {tab === "coverage" && <CoverageMap />}
        {tab === "eventsreview" && <EventsReview />}
        {tab === "dealsreview" && <DealsReview />}
        {tab === "moderation" && <Moderation />}
        {tab === "vault" && <ImageVault />}
        {tab === "newsletter" && <Newsletter />}
        {tab === "contest" && <ContestManager />}
        {tab === "subscribers" && <Subscribers />}
      </main>
    </div>
  );
}
